import React, { useCallback, useState } from 'react';

import { link } from '@aline/routes';
import { Link, Paper, Skeleton, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { format, parseISO } from 'date-fns';
import { ChevronRight, Circle, CircleCheck, ExternalLink, Mail } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useEvent } from 'react-use';
import { AlineButton } from 'src/components/generic/AlineButton';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import {
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components/generic/ConfirmationDialog';
import { Spacer } from 'src/components/generic/Spacer';
import { useIsOrgAdmin } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import TeamsIconMarkup from 'src/images/integrations/microsoft-teams.svg';
import { makeStyles } from 'tss-react/mui';

import {
  IntegrationActionButton,
  integrationHoverActionRevealStyles,
} from './IntegrationActionButton';
import { integrationCardStyles } from './IntegrationRow';

const CHECKLIST_ICON_SIZE = 13;

const SLACK_ICON_URL = 'https://a.slack-edge.com/80588/marketing/img/meta/slack_hash_256.png';
const TEAMS_ICON_URL = `data:image/svg+xml,${encodeURIComponent(TeamsIconMarkup)}`;
const MANAGE_CHANNELS_ADMIN_TOOLTIP = 'Only organization admins can manage channel integrations';
const VIEW_CHANNELS_ADMIN_HINT = 'Only organization admins can view connection details';
const EMAIL_NO_DOMAIN_HINT = 'Verify your sender domain so people can email the assistant.';
const EMAIL_VERIFY_ERROR = 'Could not start domain verification. Try again.';
const EMAIL_ACTIVATE_ERROR = 'Could not activate the email inbox. Try again.';
const EMAIL_REFRESH_ERROR = 'Could not refresh email domain status. Try again.';

/**
 * How the install CTA behaves for a provider: an unavailable placeholder, a GET
 * link that navigates to the server install route. Carried on the display so
 * RowAction reads the action off an exhaustive Record — adding a provider is a
 * compile error until it declares one, rather than silently falling through to a
 * Slack URL.
 */
type ProviderInstallCta = { href: string; kind: 'link' };

/** Provider-specific display chrome consumed by the provider-agnostic row. */
interface ProviderDisplay {
  /** What one connection of this provider is called, in the user's words. */
  readonly connectionNoun: string;
  readonly emptyHint: string;
  readonly iconUrl: string;
  readonly installCta: ProviderInstallCta;
  readonly installLabel: string;
  readonly label: string;
  /**
   * Shown when the pending step has no link to offer. Graph can omit a Teams app
   * while it propagates, when tenant policy hides it, or when an organization
   * package has not been uploaded. Older consents can also lack the application
   * permission required for the lookup.
   */
  readonly manualInstallHint: string | null;
  readonly updateHint: string | null;
  readonly updateLabel: string | null;
  /**
   * What stops working once this provider is disconnected.
   *
   * Present only where the consequence is actually known — stating a specific
   * loss the product may not have is worse than saying nothing.
   */
  readonly disconnectDetail?: string;
}

const SLACK_DISPLAY: ProviderDisplay = {
  connectionNoun: 'workspace',
  /* The exact inverse of `emptyHint`: what connecting bought, undone. */
  disconnectDetail: 'Mentioning @Aline in its channels will no longer start a thread.',
  emptyHint: 'Mention @Aline in any channel to start a thread.',
  iconUrl: SLACK_ICON_URL,
  installCta: { href: buildChannelInstallPath('slack'), kind: 'link' },
  installLabel: 'Add to Slack',
  label: 'Slack',
  manualInstallHint: null,
  updateHint: 'An update is required to use the latest Slack features.',
  updateLabel: 'Update Slack connection',
};

const TEAMS_DISPLAY: ProviderDisplay = {
  connectionNoun: 'workspace',
  emptyHint:
    'Requires a Microsoft 365 admin. Afterwards, mention @Aline in any Teams channel to start a thread.',
  iconUrl: TEAMS_ICON_URL,
  installCta: { href: buildChannelInstallPath('teams'), kind: 'link' },
  installLabel: 'Add to Teams',
  label: 'Microsoft Teams',
  manualInstallHint:
    'Setup is not finished. An Aline admin can retry setup without disconnecting. ' +
    'If setup still cannot finish, ask a Teams admin to check app availability and permissions.',
  updateHint: 'An update is required to use the latest Teams features.',
  updateLabel: 'Update Teams connection',
};

const WORKSPACE_PROVIDER_DISPLAY: Record<
  Exclude<Apollo.ChannelProvider, typeof Apollo.ChannelProvider.Resend>,
  ProviderDisplay
> = {
  [Apollo.ChannelProvider.Slack]: SLACK_DISPLAY,
  [Apollo.ChannelProvider.Teams]: TEAMS_DISPLAY,
};

const useStyles = makeStyles()(theme => ({
  action: {
    alignItems: 'flex-end',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  actionButton: {
    padding: theme.spacing(0.5, 2),
  },
  actionButtons: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
  },
  actionError: {
    padding: theme.spacing(0, 3, 2),
  },
  checklist: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  checklistDone: {
    color: theme.palette.success.main,
  },
  checklistDoneItem: {
    alignItems: 'center',
    color: theme.palette.success.main,
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  checklistHint: {
    // Align with the checklist text rather than the bullet it explains.
    paddingLeft: `calc(${CHECKLIST_ICON_SIZE}px + ${theme.spacing(0.5)})`,
  },
  checklistItem: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  checklistLink: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: theme.spacing(0.5),
  },
  checklistTodo: {
    color: theme.palette.text.disabled,
  },
  description: {
    flexGrow: 1,
    margin: theme.spacing(0.25, 1),
  },
  display: {
    alignItems: 'center',
    display: 'flex',
  },
  icon: {
    display: 'block',
    height: theme.spacing(3),
    width: theme.spacing(3),
  },
  iconBox: {
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    display: 'flex',
    flexShrink: 0,
    height: theme.spacing(5),
    justifyContent: 'center',
    position: 'relative',
    width: theme.spacing(5),
  },
  paper: integrationCardStyles(theme),
  row: {
    ...integrationHoverActionRevealStyles,
    alignItems: 'center',
    display: 'flex',
    padding: theme.spacing(1.5),
  },
  statusStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
  },
}));

interface ChannelIntegrationRowProps {
  provider: Apollo.ChannelProvider;
}

const MemoizedEmailIntegrationRow = React.memo(EmailIntegrationRow);
const MemoizedEmailIntegrationActions = React.memo(EmailIntegrationActions);
const MemoizedEmailActionButton = React.memo(EmailActionButton);
const MemoizedEmailActionButtons = React.memo(EmailActionButtons);
const MemoizedEmailStatusLine = React.memo(EmailStatusLine);
const MemoizedEmailSetupStatus = React.memo(EmailSetupStatus);
const MemoizedVerifiedDomainsSummary = React.memo(VerifiedDomainsSummary);
const MemoizedConnectedInboxSummary = React.memo(ConnectedInboxSummary);
const MemoizedEmailSetupStep = React.memo(EmailSetupStep);
const MemoizedEmailSetupIcon = React.memo(EmailSetupIcon);
const MemoizedChannelIntegrationIcon = React.memo(ChannelIntegrationIcon);

export function ChannelIntegrationRow({ provider }: ChannelIntegrationRowProps): JSX.Element {
  if (provider === Apollo.ChannelProvider.Resend) return <MemoizedEmailIntegrationRow />;
  return <WorkspaceIntegrationRow provider={provider} />;
}

function WorkspaceIntegrationRow({
  provider,
}: {
  provider: Exclude<Apollo.ChannelProvider, typeof Apollo.ChannelProvider.Resend>;
}): JSX.Element {
  const { classes } = useStyles();
  const { isOrgAdmin, loading: userLoading } = useIsOrgAdmin();
  /**
   * `channelInstallations` is admin-only on the server, so running it for anyone
   * else is a guaranteed `UnauthorizedError`. Skip until the current user
   * resolves, then skip for non-admins — `userLoading` is folded into `loading`
   * below so the row holds on a skeleton rather than flashing an unconnected
   * state at an admin whose role has not arrived yet.
   */
  const {
    data,
    loading: installsLoading,
    refetch,
  } = Apollo.useGetChannelInstallationsQuery({
    skip: userLoading || !isOrgAdmin,
  });
  const loading = userLoading || (installsLoading && !data);
  const installs =
    data?.channelInstallations.filter(installation => installation.provider === provider) ?? [];

  /**
   * A pending install is completed in another tab and confirmed by a webhook, so
   * nothing tells this page when it lands. Refetching when the user comes back
   * turns that into "it says Connected now" instead of a manual reload. Gated on
   * there being something to wait for so a settled page stays quiet.
   */
  const shouldRefreshOnFocus = isOrgAdmin && installs.some(getIsAwaitingProvider);
  useEvent('focus', () => {
    if (!shouldRefreshOnFocus) return;
    void refetch();
  });

  const display = WORKSPACE_PROVIDER_DISPLAY[provider];
  const rowInstalls = loading || installs.length === 0 ? [null] : installs;

  return (
    <Paper className={classes.paper} elevation={0}>
      {rowInstalls.map(install => (
        <div key={install?.id ?? provider} className={classes.row}>
          <div className={classes.display}>
            <MemoizedChannelIntegrationIcon iconUrl={display.iconUrl} label={display.label} />
            <div className={classes.description}>
              <Typography variant="subtitle2">{display.label}</Typography>
              <StatusLine
                display={display}
                finishInstallUrl={install?.appInstallUrl ?? null}
                install={install}
                isOrgAdmin={isOrgAdmin}
                loading={loading}
              />
            </div>
          </div>
          <Spacer />
          <RowAction
            display={display}
            finishInstallUrl={install?.appInstallUrl ?? null}
            install={install}
            isOrgAdmin={isOrgAdmin}
            loading={loading}
          />
        </div>
      ))}
    </Paper>
  );
}

type ChannelInstallationView = Pick<
  Apollo.ChannelInstallation,
  | 'id'
  | 'provider'
  | 'externalAccountId'
  | 'displayName'
  | 'installedAt'
  | 'status'
  | 'createdAt'
  | 'appInstallUrl'
  | 'requiresReauthorization'
>;

function getIsAwaitingProvider(install: ChannelInstallationView) {
  return install.status === Apollo.ChannelInstallationStatus.ActionNeeded;
}

interface StatusLineProps {
  display: ProviderDisplay;
  finishInstallUrl: string | null;
  install: ChannelInstallationView | null;
  isOrgAdmin: boolean;
  loading: boolean;
}

function StatusLine({ display, finishInstallUrl, install, isOrgAdmin, loading }: StatusLineProps) {
  const { classes } = useStyles();
  if (loading) return <Skeleton variant="text" width={220} />;
  /**
   * The admin-only query is skipped for everyone else, so `install` is null
   * whether or not the provider is connected. Falling through to the empty hint
   * would state the integration is not set up when it may well be — say what we
   * actually know instead.
   */
  if (!isOrgAdmin) {
    return (
      <Typography color="secondary" variant="caption">
        {VIEW_CHANNELS_ADMIN_HINT}
      </Typography>
    );
  }
  if (!install) {
    return (
      <Typography color="secondary" variant="caption">
        {display.emptyHint}
      </Typography>
    );
  }
  if (getIsAwaitingProvider(install))
    return <PendingChecklist display={display} finishInstallUrl={finishInstallUrl} />;

  const installedAtIso = install.installedAt ?? install.createdAt;
  const installedAt = installedAtIso ? format(parseISO(installedAtIso), 'MMM d, yyyy') : null;
  const connectedLine = (
    <Typography color="secondary" variant="caption">
      Connected to <strong>{getInstallDisplayName(install)}</strong>
      {installedAt ? ` on ${installedAt}` : null}
    </Typography>
  );
  if (install.requiresReauthorization && display.updateHint) {
    return (
      <div className={classes.statusStack}>
        {connectedLine}
        <Typography color="secondary" variant="caption">
          {display.updateHint}
        </Typography>
      </div>
    );
  }

  return connectedLine;
}

/**
 * The install is linked on our side but the app still has to be added in the
 * provider, which only the user can do. Spelling both steps out is what keeps
 * this from reading as a failure — the first step genuinely succeeded, and the
 * row flips to connected on its own once the provider reports the app installed.
 */
function PendingChecklist({
  display,
  finishInstallUrl,
}: {
  display: ProviderDisplay;
  finishInstallUrl: string | null;
}) {
  const { classes } = useStyles();
  const finishLabel = `Add the Aline app in ${display.label} to finish`;
  return (
    <div className={classes.checklist}>
      <Typography className={classes.checklistDoneItem} variant="caption">
        <CircleCheck className={classes.checklistDone} size={CHECKLIST_ICON_SIZE} />
        Linked to this organization
      </Typography>
      <Typography className={classes.checklistItem} color="secondary" variant="caption">
        <Circle className={classes.checklistTodo} size={CHECKLIST_ICON_SIZE} />
        {finishInstallUrl ? (
          <Link
            className={classes.checklistLink}
            href={finishInstallUrl}
            rel="noreferrer"
            target="_blank"
          >
            {finishLabel}
            <ExternalLink size={CHECKLIST_ICON_SIZE} />
          </Link>
        ) : (
          finishLabel
        )}
      </Typography>
      {!finishInstallUrl && Boolean(display.manualInstallHint) && (
        <Typography className={classes.checklistHint} color="secondary" variant="caption">
          {display.manualInstallHint}
        </Typography>
      )}
    </div>
  );
}

/**
 * Names the exact connection being removed.
 *
 * A row can list several installations of one provider, all offering the same
 * "Disconnect" label, so the dialog has to say which one it means — the name
 * is what tells two Slack workspaces apart.
 */
function getDisconnectMessage(display: ProviderDisplay, install: ChannelInstallationView): string {
  const target = `the ${display.label} ${display.connectionNoun} ${getInstallDisplayName(install)}`;
  const stops = `Aline stops using ${target}.`;
  return display.disconnectDetail ? `${stops} ${display.disconnectDetail}` : stops;
}

function getInstallDisplayName(install: ChannelInstallationView) {
  return install.displayName ?? install.externalAccountId;
}

interface RowActionProps {
  display: ProviderDisplay;
  finishInstallUrl: string | null;
  install: ChannelInstallationView | null;
  isOrgAdmin: boolean;
  loading: boolean;
}

function RowAction({ display, finishInstallUrl, install, isOrgAdmin, loading }: RowActionProps) {
  if (loading) return null;
  if (install)
    return (
      <InstalledActions
        display={display}
        finishInstallUrl={finishInstallUrl}
        install={install}
        isOrgAdmin={isOrgAdmin}
      />
    );

  const { installCta, installLabel } = display;

  /**
   * Shown but inert for non-admins rather than hidden: the integration exists
   * either way, and a missing row reads as "we don't offer this" instead of
   * "someone else has to do it". Uniform across providers, matching the single
   * admin check in `authorizeChannelInstallRequest`.
   */
  if (!isOrgAdmin) {
    return (
      <AdminGatedControl>
        <IntegrationActionButton disabled>{installLabel}</IntegrationActionButton>
      </AdminGatedControl>
    );
  }

  return (
    <IntegrationActionButton component="a" href={installCta.href}>
      {installLabel}
    </IntegrationActionButton>
  );
}

/**
 * A lone action stands on its own; several sit side by side in a labelled row so
 * they read as one control set instead of a stack.
 */
function RowActionGroup({ children, label }: { children: React.ReactNode; label: string }) {
  const { classes } = useStyles();
  const buttons = React.Children.toArray(children);
  if (buttons.length <= 1) return buttons[0] ?? null;
  return (
    <div aria-label={label} className={classes.actionButtons} role="group">
      {children}
    </div>
  );
}

/** Wraps a disabled control so the reason it is disabled is discoverable on hover. */
function AdminGatedControl({ children }: { children: JSX.Element }) {
  return (
    <AlineTooltip arrow placement="top" title={MANAGE_CHANNELS_ADMIN_TOOLTIP}>
      <span>{children}</span>
    </AlineTooltip>
  );
}

function buildChannelInstallPath(provider: 'slack' | 'teams') {
  return link.ApiIntegrationsChatProviderInstall({ provider });
}

type InstalledAction =
  | {
      disabled: boolean;
      href: string | null;
      key: 'finishSetup' | 'updateConnection';
      kind: 'link';
      label: string;
      revealOnHover?: boolean;
      target?: '_blank';
    }
  | { disabled: boolean; key: 'disconnect'; kind: 'button'; label: string; onClick: () => void };

function InstalledActions({
  display,
  finishInstallUrl,
  install,
  isOrgAdmin,
}: {
  display: ProviderDisplay;
  finishInstallUrl: string | null;
  install: ChannelInstallationView;
  isOrgAdmin: boolean;
}) {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [confirming, setConfirming] = useState(false);
  const [disconnect, { loading: disconnecting }] = Apollo.useDisconnectChannelInstallationMutation({
    refetchQueries: [Apollo.GetChannelInstallationsDocument],
  });
  const onConfirmDisconnect = useCallback(async () => {
    try {
      await disconnect({ variables: { id: install.id } });
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar(`Could not disconnect ${getInstallDisplayName(install)}. Try again.`, {
        variant: 'error',
      });
    }
  }, [disconnect, enqueueSnackbar, install]);

  const availableActions: InstalledAction[] = [];
  if (getIsAwaitingProvider(install)) {
    const retryInstallUrl = display.installCta.kind === 'link' ? display.installCta.href : null;
    availableActions.push({
      disabled: !finishInstallUrl && (!isOrgAdmin || !retryInstallUrl),
      href: finishInstallUrl ?? (isOrgAdmin ? retryInstallUrl : null),
      key: 'finishSetup',
      kind: 'link',
      label: finishInstallUrl ? 'Finish setup' : 'Retry setup',
      target: finishInstallUrl ? '_blank' : undefined,
    });
  } else if (install.requiresReauthorization && display.updateLabel) {
    const updateHref = display.installCta.kind === 'link' ? display.installCta.href : null;
    availableActions.push({
      disabled: !isOrgAdmin || !updateHref,
      href: isOrgAdmin ? updateHref : null,
      key: 'updateConnection',
      kind: 'link',
      label: display.updateLabel,
      revealOnHover: false,
    });
  }
  availableActions.push({
    disabled: !isOrgAdmin || disconnecting,
    key: 'disconnect',
    kind: 'button',
    label: 'Disconnect',
    onClick: () => setConfirming(true),
  });
  return (
    <div className={classes.action}>
      <RowActionGroup label={`${display.label} integration actions`}>
        {availableActions.map(action => (
          <InstalledActionButton key={action.key} action={action} />
        ))}
      </RowActionGroup>
      <ConfirmationDialog
        message={getDisconnectMessage(display, install)}
        open={confirming}
        primaryText="Disconnect"
        title={`Disconnect ${display.label}?`}
        variant={ConfirmationDialogVariant.Warn}
        onClickPrimary={onConfirmDisconnect}
        onClose={() => setConfirming(false)}
      />
    </div>
  );
}

function EmailIntegrationRow(): JSX.Element {
  const { classes } = useStyles();
  const { isOrgAdmin, loading: userLoading } = useIsOrgAdmin();
  const {
    data,
    loading: intakeLoading,
    refetch,
  } = Apollo.useGetOrgEmailIntakeQuery({
    skip: userLoading || !isOrgAdmin,
  });
  const loading = userLoading || intakeLoading;
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const shouldRefreshOnFocus =
    isOrgAdmin &&
    Boolean(
      data?.orgEmailIntake.domains.some(
        domain => domain.status !== Apollo.EmailDomainVerificationStatus.Verified
      )
    );
  useEvent('focus', () => {
    if (!shouldRefreshOnFocus) return;
    void refetch().then(
      () => setRefreshError(null),
      () => setRefreshError(EMAIL_REFRESH_ERROR)
    );
  });

  const intake = data?.orgEmailIntake;
  /**
   * Derived once and shared, so the checklist and the buttons can never disagree
   * about which step is outstanding.
   */
  const setupState = intake ? getEmailSetupState(intake) : null;

  return (
    <Paper className={classes.paper} elevation={0}>
      <div className={classes.row}>
        <div className={classes.display}>
          <MemoizedChannelIntegrationIcon label="Email" />
          <div className={classes.description}>
            <Typography variant="subtitle2">Email</Typography>
            <MemoizedEmailStatusLine isOrgAdmin={isOrgAdmin} loading={loading} state={setupState} />
          </div>
        </div>
        <Spacer />
        <MemoizedEmailIntegrationActions
          isOrgAdmin={isOrgAdmin}
          loading={loading}
          state={setupState}
        />
      </div>
      {Boolean(refreshError) && (
        <Typography className={classes.actionError} color="error" variant="caption">
          {refreshError}
        </Typography>
      )}
    </Paper>
  );
}

function ChannelIntegrationIcon({ iconUrl, label }: { label: string; iconUrl?: string }) {
  const { classes } = useStyles();
  return (
    <div className={classes.iconBox}>
      <ChannelIntegrationGlyph className={classes.icon} iconUrl={iconUrl} label={label} />
    </div>
  );
}

function ChannelIntegrationGlyph({
  className,
  iconUrl,
  label,
}: {
  className: string;
  label: string;
  iconUrl?: string;
}) {
  if (iconUrl) return <img alt={label} className={className} src={iconUrl} />;
  return <Mail aria-label={label} className={className} />;
}

function EmailIntegrationActions({
  isOrgAdmin,
  loading,
  state,
}: {
  isOrgAdmin: boolean;
  loading: boolean;
  state: EmailSetupState | null;
}) {
  const { classes } = useStyles();
  const [actionError, setActionError] = useState<string | null>(null);
  const [startVerification, { loading: starting }] =
    Apollo.useStartEmailDomainVerificationMutation();
  const [setEnabled, { loading: toggling }] = Apollo.useSetChannelAssistantEmailEnabledMutation({
    refetchQueries: [Apollo.GetOrgEmailIntakeDocument],
  });

  const onVerifyDomain = useCallback(() => {
    setActionError(null);
    void startVerification().then(
      result => {
        const portalUrl = result.data?.startEmailDomainVerification;
        if (portalUrl) window.location.href = portalUrl;
        else setActionError(EMAIL_VERIFY_ERROR);
      },
      () => setActionError(EMAIL_VERIFY_ERROR)
    );
  }, [startVerification]);

  const onActivateInbox = useCallback(() => {
    setActionError(null);
    void setEnabled({ variables: { enabled: true } }).catch(() =>
      setActionError(EMAIL_ACTIVATE_ERROR)
    );
  }, [setEnabled]);

  /**
   * Shown but inert for non-admins, the same as an uninstalled Slack or Teams
   * row. The intake query is skipped for them, so verification is the only step
   * we can honestly name.
   */
  if (!isOrgAdmin) {
    return (
      <div className={classes.action}>
        <AdminGatedControl>
          <AlineButton
            disabled
            buttonClassName={classes.actionButton}
            color="primary"
            variant="outlined"
          >
            Verify domain
          </AlineButton>
        </AdminGatedControl>
      </div>
    );
  }

  return (
    <div className={classes.action}>
      <MemoizedEmailActionButtons
        loading={loading}
        starting={starting}
        state={state}
        toggling={toggling}
        onActivateInbox={onActivateInbox}
        onVerifyDomain={onVerifyDomain}
      />
      {Boolean(actionError) && (
        <Typography className={classes.actionError} color="error" variant="caption">
          {actionError}
        </Typography>
      )}
    </div>
  );
}

interface EmailAction {
  disabled: boolean;
  key: 'activateInbox' | 'addDomain' | 'verifyDomain';
  label: string;
  onClick: () => void;
  variant: 'outlined' | 'text';
}

function EmailActionButton({ action }: { action: EmailAction }) {
  const { classes } = useStyles();
  return (
    <AlineButton
      buttonClassName={classes.actionButton}
      color="primary"
      disabled={action.disabled}
      variant={action.variant}
      onClick={action.onClick}
    >
      {action.label}
    </AlineButton>
  );
}

function EmailActionButtons({
  loading,
  onActivateInbox,
  onVerifyDomain,
  starting,
  state,
  toggling,
}: {
  loading: boolean;
  onActivateInbox: () => void;
  onVerifyDomain: () => void;
  starting: boolean;
  state: EmailSetupState | null;
  toggling: boolean;
}) {
  const actions = getEmailActions({
    loading,
    onActivateInbox,
    onVerifyDomain,
    starting,
    state,
    toggling,
  });
  return (
    <RowActionGroup label="Email integration actions">
      {actions.map(action => (
        <MemoizedEmailActionButton key={action.key} action={action} />
      ))}
    </RowActionGroup>
  );
}

function getEmailActions({
  loading,
  onActivateInbox,
  onVerifyDomain,
  starting,
  state,
  toggling,
}: {
  loading: boolean;
  onActivateInbox: () => void;
  onVerifyDomain: () => void;
  starting: boolean;
  state: EmailSetupState | null;
  toggling: boolean;
}): EmailAction[] {
  const primaryAction = getEmailPrimaryAction(state);
  const actions: EmailAction[] = [];
  if (primaryAction === 'verifyDomain')
    actions.push({
      /**
       * `loading` matters as much as `starting`: an unread intake state also
       * reads as "needs a domain", so an org that finished setup would be
       * offered a live button into the WorkOS portal until the query lands.
       */
      disabled: starting || loading,
      key: 'verifyDomain',
      label: 'Verify domain',
      onClick: onVerifyDomain,
      variant: 'outlined',
    });
  if (primaryAction === 'activateInbox')
    actions.push({
      disabled: toggling || loading,
      key: 'activateInbox',
      label: 'Activate inbox',
      onClick: onActivateInbox,
      variant: 'outlined',
    });
  if (primaryAction !== 'verifyDomain')
    actions.push({
      disabled: starting,
      key: 'addDomain',
      label: 'Add another domain',
      onClick: onVerifyDomain,
      variant: 'text',
    });
  return actions;
}

function EmailStatusLine({
  isOrgAdmin,
  loading,
  state,
}: {
  isOrgAdmin: boolean;
  loading: boolean;
  state: EmailSetupState | null;
}) {
  if (loading) return <Skeleton variant="text" width={220} />;
  if (!isOrgAdmin)
    return (
      <Typography color="secondary" variant="caption">
        {VIEW_CHANNELS_ADMIN_HINT}
      </Typography>
    );
  if (!state)
    return (
      <Typography color="secondary" variant="caption">
        {EMAIL_NO_DOMAIN_HINT}
      </Typography>
    );
  return <MemoizedEmailSetupStatus state={state} />;
}

/**
 * Finished setup collapses to one line rather than a checklist of ticks. An org
 * that verified its domain elsewhere — SSO, or an earlier inbox — arrives here
 * already past that step, and re-listing it as a thing to do reads as if the
 * verification did not count.
 */
function EmailSetupStatus({ state }: { state: EmailSetupState }) {
  const { classes } = useStyles();

  if (state.kind === 'needsDomain')
    return <MemoizedEmailSetupStep done={false}>{state.hint}</MemoizedEmailSetupStep>;

  if (state.kind === 'needsInbox') {
    return (
      <div className={classes.checklist}>
        <MemoizedEmailSetupStep done>
          <MemoizedVerifiedDomainsSummary domains={state.verifiedDomains} />
        </MemoizedEmailSetupStep>
        <MemoizedEmailSetupStep done={false}>Create your Aline inbox</MemoizedEmailSetupStep>
      </div>
    );
  }

  return (
    <MemoizedEmailSetupStep done>
      <MemoizedConnectedInboxSummary state={state} />
    </MemoizedEmailSetupStep>
  );
}

function VerifiedDomainsSummary({ domains }: { domains: string[] }) {
  const label = domains.length > 1 ? 'Domains verified' : 'Domain verified';
  return (
    <>
      {label}: <strong>{domains.join(', ')}</strong>
    </>
  );
}

function ConnectedInboxSummary({ state }: { state: ReadyEmailSetupState }) {
  const domains = state.verifiedDomains.join(', ');
  const lead = state.hasReceivedMail ? 'Receiving email' : 'Ready to receive email';
  if (!state.inboxAddress) return <>{`${lead} from ${domains}`}</>;
  return (
    <>
      {lead} at <strong>{state.inboxAddress}</strong> from <strong>{domains}</strong>
    </>
  );
}

function EmailSetupStep({ children, done }: { children: React.ReactNode; done: boolean }) {
  const { classes } = useStyles();
  return (
    <Typography className={classes.checklistItem} color="secondary" variant="caption">
      <MemoizedEmailSetupIcon done={done} />
      {children}
    </Typography>
  );
}

function EmailSetupIcon({ done }: { done: boolean }) {
  const { classes } = useStyles();
  if (done) return <CircleCheck className={classes.checklistDone} size={CHECKLIST_ICON_SIZE} />;
  return <Circle className={classes.checklistTodo} size={CHECKLIST_ICON_SIZE} />;
}

interface ReadyEmailSetupState {
  hasReceivedMail: boolean;
  inboxAddress: string | null;
  kind: 'ready';
  verifiedDomains: string[];
}

/**
 * The one step the org still owes, so nothing has to re-derive "is this done?"
 * from raw domain rows at each call site.
 */
type EmailSetupState =
  | { hint: string; kind: 'needsDomain' }
  | { kind: 'needsInbox'; verifiedDomains: string[] }
  | ReadyEmailSetupState;

function getEmailSetupState(intake: Apollo.OrgEmailIntakeSimpleFragment): EmailSetupState {
  const verified = intake.domains.filter(
    domain => domain.status === Apollo.EmailDomainVerificationStatus.Verified
  );
  if (!verified.length)
    return { hint: getDomainVerificationHint(intake.domains), kind: 'needsDomain' };

  const verifiedDomains = verified.map(domain => domain.domain);
  if (!intake.enabled) return { kind: 'needsInbox', verifiedDomains };

  return {
    hasReceivedMail: verified.some(domain => Boolean(domain.confirmedAt)),
    inboxAddress: intake.inboxAddress,
    kind: 'ready',
    verifiedDomains,
  };
}

/**
 * Null state means the intake query was skipped or has not landed, which reads
 * the same as having no domain: the only thing on offer is verifying one.
 */
function getEmailPrimaryAction(state: EmailSetupState | null) {
  if (!state || state.kind === 'needsDomain') return 'verifyDomain';
  if (state.kind === 'needsInbox') return 'activateInbox';
  return null;
}

function getDomainVerificationHint(domains: Apollo.OrgEmailIntakeSimpleFragment['domains']) {
  const failed = domains.find(
    domain => domain.status === Apollo.EmailDomainVerificationStatus.Failed
  );
  if (failed) return `Verification failed for ${failed.domain}`;
  const pending = domains.find(
    domain => domain.status === Apollo.EmailDomainVerificationStatus.Pending
  );
  if (pending) return `Verification pending for ${pending.domain}`;
  return EMAIL_NO_DOMAIN_HINT;
}

function InstalledActionButton({ action }: { action: InstalledAction }) {
  switch (action.kind) {
    case 'link':
      return (
        <IntegrationActionButton
          component="a"
          disabled={action.disabled}
          endIcon={<ChevronRight size={18} />}
          href={action.href ?? undefined}
          rel={action.target === '_blank' ? 'noreferrer' : undefined}
          revealOnHover={action.revealOnHover}
          target={action.target}
        >
          {action.label}
        </IntegrationActionButton>
      );
    case 'button':
      return (
        <IntegrationActionButton disabled={action.disabled} onClick={action.onClick}>
          {action.label}
        </IntegrationActionButton>
      );
  }
}
